import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  registerNewCustomer,
  editCustomerInfo,
  createAccount,
  editAccount,
  getStationsByPostalCode,
} from "redux/actions/customers/customersAction";
import { useForm } from "hooks/useForm";
import { useHandleView } from "../useHandleView";
import { validateObject } from "helpers/validateObject";
import { segmentsTypesOpt, typesUnitsOpt } from "helpers/dictionary";
import { fieldValidations, initialValues } from "./inputValues";

export const useCustomerForm = (searchInput = "", onClose) => {
  const {
    account,
    customer,
    addMode,
    newCustomerMode,
    editCustomer,
    amazonConnectCall,
  } = useSelector(({ customersReducer }) => customersReducer);
  const { stationsList } = useSelector(({ companies }) => companies);
  const handleView = useHandleView(1);
  const dispatch = useDispatch();
  const [location, setLocation] = useState({
    latitude: 0,
    longitude: 0,
  });
  const [userStation, setUserStation] = useState();
  const { latitude, longitude } = location;

  const {
    inputValues,
    handleInputOnChange,
    checkboxOnChange,
    cleanForm,
    handleNewValues,
    fieldErrors,
    runValidates,
  } = useForm(initialValues, fieldValidations(addMode));
  const {
    name,
    rfc,
    isWholesaler,
    phone,
    street,
    extNumber,
    intNumber,
    colony,
    municipality,
    postalCode,
    state,
    reference,
    idStation,
    type,
    segment,
    segmentsOpt,
    subsegment,
    factor,
    isVerified,
  } = inputValues;

  const handleSearchAddress = (value) => {
    try {
      cleanForm();
      const addressData = value?.reduce((accumulator, item) => {
        const type = item.types[0];

        switch (type) {
          case "street_number":
            return { ...accumulator, extNumber: item.long_name };
          case "route":
            return { ...accumulator, street: item.long_name };
          case "sublocality_level_1":
          case "sublocality":
          case "political":
            return { ...accumulator, colony: item.long_name };
          case "locality":
            return { ...accumulator, municipality: item.long_name };
          case "administrative_area_level_1":
            return { ...accumulator, state: item.long_name };
          case "country":
            return { ...accumulator, country: item.long_name };
          case "postal_code":
            return { ...accumulator, postalCode: item.long_name };
          default:
            return accumulator;
        }
      }, {});
      const allValues = { ...inputValues, ...addressData };
      handleNewValues(allValues);
    } catch (error) {
      console.error("Error al obtener la dirección:", error);
    }
  };

  const handleUnitType = (value) => {
    const segments = segmentsTypesOpt.find((seg) => seg.type === value?.value);
    handleNewValues({
      type: value,
      segment: segments || null,
      subsegment: segments?.subsegments[0] || [],
      segmentsOpt: [segments] || [],
    });
  };

  const handleSubmit = () => {
    if (editCustomer) {
      if (runValidates(["idStation"])) return;
    } else {
      if (runValidates(["rfc"])) return;
    }

    const addressValues = {
      colony,
      extNumber,
      intNumber,
      latitude,
      longitude,
      municipality,
      postalCode,
      reference,
      state,
      street,
    };
    const form = {
      address: addressValues,
      idStation: idStation?.id,
      name,
      phone,
      segment: segment.value,
      subsegment: subsegment.value,
      type: type.value,
      localPhone: phone,
      idCustomer: customer?.id,
      rfc,
      isWholesaler: isWholesaler ? true : false,
      factor: factor?.realValue,
      isVerified: !!isVerified,
    };
    if (newCustomerMode) {
      const { idCustomer: _, ...rest } = form;
      dispatch(registerNewCustomer(rest, onClose));
      return;
    }
    if (editCustomer) {
      const editFormCustomer = {
        address: {
          colony,
          extNumber,
          intNumber,
          latitude,
          longitude,
          municipality,
          postalCode,
          reference,
          state,
          street,
        },
        id: customer.id,
        name,
        phone,
        rfc,
        isWholesaler: isWholesaler ? true : false,
        idStation: idStation?.id,
      };

      dispatch(editCustomerInfo(customer.id, editFormCustomer));
    } else {
      addMode
        ? dispatch(createAccount(customer.id, form, handleView))
        : dispatch(editAccount(customer.id, account.id, form, handleView));
      return;
    }
  };

  const handleChange = (e, value) => {
    handleNewValues({
      ...inputValues,
      idStation: value.id,
    });
  };

  const validatePhone = (value) => {
    const expr = /^[0-9]+$/;
    return expr.test(value);
  };

  const createFactorOpt = useCallback(() => {
    let arr = [];
    for (let i = 0; i <= 20; i++) {
      arr.push({
        label: `${i}`,
        value: `${i}`,
        realValue: i,
      });
    }
    arr.unshift({
      label: "Sin giro",
      value: null,
    });
    return arr;
  }, []);

  useEffect(() => {
    if (searchInput.length === 10 && validatePhone(searchInput)) {
      handleNewValues({
        phone: searchInput,
      });
    }
    //eslint-disabled-next-line
  }, [searchInput]);

  useEffect(() => {
    if (validateObject(account)) {
      const factor = createFactorOpt();
      const segment =
        segmentsTypesOpt.find((sg) => sg.type === account.type) || null;

      const newValues = {
        name: account.alias,
        rfc: customer.rfc,
        isWholesaler: customer.isWholesaler,
        phone: account.localPhone,
        street: account.address.street,
        extNumber: account.address.extNumber,
        intNumber: account.address.intNumber,
        colony: account.address.colony,
        municipality: account.address.municipality,
        postalCode: account.address.postalCode,
        state: account.address.state,
        reference: account.address.reference,
        idStation: stationsList.find((sta) => sta.id === account.idStation),
        type: typesUnitsOpt.find((uType) => uType.value === account.type),
        segment: segment,
        segmentsOpt: segment ? [segment] : [],
        subsegment:
          segment?.subsegments.find(
            (sbs) => sbs.value === account.subsegment
          ) || null,
        factor: factor.find((acc) => acc.realValue === account.factor) || null,
        isVerified: account.isVerified ?? false,
      };

      setLocation({
        latitude: account.address.latitude,
        longitude: account.address.longitude,
      });
      handleNewValues(newValues);
    }
    if (editCustomer) {
      const newValues = {
        name: customer.name,
        rfc: customer.rfc,
        isWholesaler: customer.isWholesaler,
        phone: customer.phone,
        street: customer.address?.street,
        extNumber: customer.address?.extNumber,
        intNumber: customer.address?.intNumber,
        colony: customer.address?.colony,
        municipality: customer.address?.municipality,
        postalCode: customer.address?.postalCode,
        state: customer.address?.state,
        reference: customer.address?.reference,
        idStation: stationsList.find((sta) => sta.id === account.idStation),
      };
      handleNewValues(newValues);
    }
    //eslint-disable-next-line
  }, [account, editCustomer]);

  useEffect(() => {
    if (addMode) {
      cleanForm();
      setLocation({
        latitude: 0,
        longitude: 0,
      });
    }
    //eslint-disable-next-line
  }, [addMode]);

  useEffect(() => {
    if (postalCode && postalCode.length === 5) {
      dispatch(getStationsByPostalCode(postalCode));
    }
    //eslint-disabled-next-line
  }, [postalCode]);

  useEffect(
    () => {
      newCustomerMode &&
        handleNewValues({
          ...inputValues,
          phone: amazonConnectCall?.phone
            ? amazonConnectCall?.phone
            : searchInput,
        });
    },
    //eslint-disabled-next-line
    []
  );

  return {
    segmentsOpt,
    handleUnitType,
    setLocation,
    location,
    userStation,
    setUserStation,
    handleSearchAddress,
    handleSubmit,
    values: inputValues,
    handleInputOnChange,
    checkboxOnChange,
    fieldErrors,
    handleChange,
    handleNewValues,
    createFactorOpt,
  };
};
